import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { FileText, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';

// Worker pdf.js servi comme fichier statique depuis /public (méthode la plus
// fiable, sans dépendre du bundling Vite). Le fichier doit correspondre à la
// version de pdfjs-dist installée.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Bouton qui ouvre une fenêtre modale affichant un PDF.
 * Rendu principal via pdf.js ; repli automatique sur la visionneuse
 * native du navigateur (iframe) en cas d'échec ou de lenteur.
 */
export default function PdfViewer({ url, title, buttonLabel }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let renderedAny = false;

    setLoading(true);
    setFallback(false);
    setNumPages(0);

    // Garde-fou : si rien n'est rendu après 10s, on bascule sur l'iframe natif.
    const watchdog = setTimeout(() => {
      if (!cancelled && !renderedAny) {
        // eslint-disable-next-line no-console
        console.error('[PdfViewer] pdf.js trop lent, bascule sur le lecteur natif.');
        setFallback(true);
        setLoading(false);
      }
    }, 10000);

    // On lance le chargement immédiatement (indépendant du conteneur DOM).
    const loadingTask = pdfjsLib.getDocument(url);

    loadingTask.promise
      .then(async (pdf) => {
        if (cancelled) return;
        setNumPages(pdf.numPages);

        // À ce stade le conteneur est monté dans le DOM.
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = '';

        const availableWidth = (container.clientWidth || 600) - 8;
        const dpr = window.devicePixelRatio || 1;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) break;
          // eslint-disable-next-line no-await-in-loop
          const page = await pdf.getPage(pageNum);

          const baseViewport = page.getViewport({ scale: 1 });
          const scale = availableWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.className = 'mx-auto mb-4 rounded bg-white shadow-md';
          const context = canvas.getContext('2d');

          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = '100%';
          canvas.style.maxWidth = `${Math.floor(viewport.width)}px`;
          canvas.style.height = 'auto';

          container.appendChild(canvas);

          // eslint-disable-next-line no-await-in-loop
          await page.render({
            canvasContext: context,
            viewport,
            transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
          }).promise;

          renderedAny = true;
          if (!cancelled) setLoading(false);
        }

        if (!cancelled) setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[PdfViewer] Échec pdf.js, bascule sur le lecteur natif :', err);
        setFallback(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      loadingTask.destroy?.();
    };
  }, [open, url]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl">
          <FileText className="w-4 h-4 mr-2" />
          {buttonLabel}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0 border-b pl-6 pr-14 py-4">
          <DialogTitle className="text-base md:text-lg text-left">
            {title}
            {numPages > 0 && !fallback && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({numPages} page{numPages > 1 ? 's' : ''})
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Aperçu du document {title}
          </DialogDescription>
          <Button asChild variant="outline" size="sm" className="rounded-lg shrink-0">
            <a href={url} download>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </a>
          </Button>
        </DialogHeader>

        <div className="relative flex-1 overflow-hidden bg-muted/30">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground z-10">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Chargement du document…</span>
            </div>
          )}

          {fallback ? (
            <iframe
              src={url}
              title={title}
              className="w-full h-full border-0"
            />
          ) : (
            <div ref={containerRef} className="absolute inset-0 overflow-y-auto p-4" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
